import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import ArcadeNav from "../arcade/ArcadeNav";
import ArcadeFooter from "../arcade/ArcadeFooter";
import useSEO from "../seo/useSEO";
import "./legal.css";

const CONTACT_EMAIL = "hello@zombiecollector3d.com";
const LAST_UPDATED = "June 26, 2026";

export default function TermsPage() {
  useSEO({
    title: "Terms of Service — Zombie Arcade",
    description:
      "The rules for using Zombie Arcade — Zombie Collector 3D, Zombie Solitaire, Zombie Crush, Zombie Merge, and Zombie Idle Tycoon. Fair, plain-English, no lawyer-speak.",
    canonical: "https://zombiecollector3d.com/terms",
  });

  return (
    <div className="legal-page" data-testid="terms-page">
      <ArcadeNav />
      <main className="legal-main">
        <Link to="/" className="legal-back" data-testid="terms-back">
          <ArrowLeft size={16} /> Back to Arcade
        </Link>

        <header className="legal-hero">
          <h1>Terms of Service</h1>
          <p className="subtitle">
            The rules for using Zombie Arcade. Boring, but short.
          </p>
          <p className="legal-updated">Last updated: {LAST_UPDATED}</p>
        </header>

        <section className="legal-section">
          <h2>1. What this is</h2>
          <p>
            These Terms of Service (&quot;Terms&quot;) govern your use of{" "}
            <strong>zombiecollector3d.com</strong> and every game hosted on it
            (&quot;Zombie Arcade&quot;, the &quot;Site&quot;, &quot;we&quot;, &quot;us&quot;). By playing, signing
            in, or making a purchase you agree to these Terms. If you
            don&apos;t agree, please don&apos;t use the Site.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Who can play</h2>
          <p>
            You must be at least 13 years old to create an account. If
            you&apos;re between 13 and 18, you may only use the Site with
            permission from a parent or legal guardian, and you may not
            make purchases without their consent. Zombie Arcade is not
            directed at children under 13.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Your account</h2>
          <ul>
            <li>You&apos;re responsible for keeping your Google sign-in secure.</li>
            <li>One account per person. Multi-accounting for leaderboard advantage or reward farming will result in permanent bans.</li>
            <li>Don&apos;t share your account or sell it. Cheating, exploiting bugs for gain, or manipulating in-game currency will get your account terminated without refund.</li>
            <li>Choose a display name that isn&apos;t offensive, threatening, or impersonates someone else. We reserve the right to change display names that violate this.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>4. Purchases &mdash; VIP, Founder, and coin packs</h2>
          <p>
            Zombie Arcade offers optional paid upgrades processed by Stripe:
          </p>
          <ul>
            <li><strong>VIP Weekly</strong> ($0.99) &mdash; 7-day pass, one-time payment, does not auto-renew.</li>
            <li><strong>VIP Monthly</strong> ($2.99) &mdash; 30-day pass, one-time payment, does not auto-renew.</li>
            <li><strong>VIP Lifetime</strong> ($19.99) &mdash; Permanent VIP status.</li>
            <li><strong>Founder Pack</strong> ($29.99) &mdash; Permanent VIP + Founder badge + Founder title, one-time.</li>
            <li><strong>Coin packs</strong> &mdash; In-game currency, delivered instantly on purchase.</li>
          </ul>
          <h3>4.1 Refunds</h3>
          <p>
            Because digital content and in-game currency are delivered
            immediately and cannot be &quot;returned&quot;, <strong>all sales are final</strong>.
            That said, if you were charged in error, if the service failed
            to deliver, or if a purchase was made without your consent by
            a family member, email us at{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> within 14
            days and we will investigate in good faith and issue a refund
            if appropriate.
          </p>
          <h3>4.2 Price changes</h3>
          <p>
            We may change prices for future purchases. Any pass you already
            own will continue to work for its full remaining duration at
            the price you paid.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. In-game currency and items</h2>
          <p>
            Coins, gems, tokens, cosmetics, XP, talents, and any other
            in-game items exist only inside Zombie Arcade. They have no
            cash value, cannot be exchanged for real money, and cannot be
            transferred between accounts. We may adjust game economies,
            drop rates, and item balance at any time to keep gameplay
            fair.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Acceptable use</h2>
          <p>You agree NOT to:</p>
          <ul>
            <li>Use bots, automation, memory editors, or macros to play.</li>
            <li>Reverse-engineer or scrape the game code beyond what&apos;s legally required.</li>
            <li>Attack, spam, or attempt to gain unauthorised access to our servers.</li>
            <li>Post content on leaderboards or in usernames that is illegal, hateful, sexual, or infringes anyone&apos;s rights.</li>
            <li>Impersonate Zombie Arcade staff.</li>
            <li>Use the Site for anything other than personal, non-commercial gameplay.</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>7. Content ownership</h2>
          <p>
            All game code, art, music, characters, and text on the Site
            are owned by Zombie Arcade or its licensors and are protected
            by copyright and trademark law. You may take screenshots and
            create fan videos / streams for non-commercial personal use.
            You may not redistribute, modify, or sell any of our assets.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Service availability &mdash; no guarantees</h2>
          <p>
            We try hard to keep Zombie Arcade online 24/7, but the Site
            is provided <strong>&quot;as is&quot; and &quot;as available&quot;</strong>. We do not
            guarantee uninterrupted service, and we may add, change, or
            remove games and features at any time. To the fullest extent
            permitted by law, Zombie Arcade is not liable for lost
            progress, lost currency, downtime, or indirect damages.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Termination</h2>
          <p>
            You may stop playing and request account deletion at any time
            by emailing{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. We may
            suspend or terminate your account if you violate these Terms.
            If we permanently ban you for cheating or fraud, we are not
            required to refund past purchases.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Governing law</h2>
          <p>
            These Terms are governed by the laws of the{" "}
            <strong>State of Alabama, United States</strong>, without regard to
            conflict-of-laws principles. Any dispute that cannot be
            resolved by emailing us first will be submitted to the state
            or federal courts located in Alabama, and you consent to
            personal jurisdiction there.
          </p>
        </section>

        <section className="legal-section">
          <h2>11. Changes to these terms</h2>
          <p>
            If we materially change these Terms, we&apos;ll update the &quot;Last
            updated&quot; date at the top and, if you have an account, we&apos;ll
            show you a notice on your next sign-in. Continued use after
            an update means you accept the new Terms.
          </p>
        </section>

        <section className="legal-section">
          <h2>12. Contact</h2>
          <p>
            Questions about these Terms:{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </p>
        </section>

        <ArcadeFooter />
      </main>
    </div>
  );
}
